import Phaser from "phaser";

const HISTORY_CONTENT = {
  es: {
    sceneLabel: "ARCHIVO DEL PROYECTO",
    title: "La historia detrás de Verde Origen",
    intro:
      "Lo que descubras aquí aparecerá después, transformado en preguntas, durante tu partida.",
    previous: "ANTERIOR",
    next: "SIGUIENTE",
    back: "VOLVER",
    finish: "LISTO PARA JUGAR",
    page: "ARCHIVO",
    pages: [
      {
        eyebrow: "MEDELLÍN · AGRICULTURA URBANA",
        title: "Una ciudad que también cultiva",
        blocks: [
          {
            title: "El reto",
            body: "La agricultura urbana consiste en cultivar alimentos dentro o cerca de las ciudades. Acerca la producción a quienes consumen los alimentos y permite aprovechar huertas, jardines y otros espacios urbanos.",
          },
          {
            title: "¿Qué significa hacerlo de forma sostenible?",
            body: "Satisfacer las necesidades actuales sin comprometer los recursos ni el bienestar de las futuras generaciones. En agricultura, eso implica producir bien mientras reducimos impactos sobre el agua, el suelo y los ecosistemas.",
          },
          {
            title: "La misión en Medellín",
            body: "Universidad CES, con financiación de Ruta N y el Jardín Botánico de Medellín como aliado, evalúa un biofertilizante de origen local en el Laboratorio de Agricultura Urbana. La meta es encontrar soluciones limpias, eficaces y adaptadas a las necesidades de la ciudad.",
          },
        ],
        key: "CLAVE · Agricultura urbana = cultivar dentro o cerca de la ciudad. Sostenibilidad = producir hoy sin cerrar las posibilidades del mañana.",
      },
      {
        eyebrow: "BIOINSUMOS · NUTRICIÓN VEGETAL",
        title: "¿Qué es un biofertilizante?",
        blocks: [
          {
            title: "Alimento de origen biológico",
            body: "Un biofertilizante es un insumo agrícola de origen biológico. Puede aportar nutrientes y compuestos naturales que favorecen el crecimiento y desarrollo de las plantas. No es simplemente un fertilizante sintético con otro nombre.",
          },
          {
            title: "Nutrientes + compuestos bioactivos",
            body: "Los nutrientes son materiales que la planta necesita para crecer. Un compuesto bioactivo es una sustancia natural capaz de influir en procesos de la planta. Un biofertilizante puede aportar nutrientes y también favorecer procesos que mejoran su absorción.",
          },
          {
            title: "¿Por qué interesa?",
            body: "Alternativas biológicas pueden ayudar a reducir la dependencia de insumos sintéticos. El reto no es aplicar más producto, sino aportar lo necesario: suficiente para nutrir el cultivo, sin desperdiciar recursos ni contaminar.",
          },
        ],
        key: "CLAVE · Un buen manejo de nutrientes busca equilibrio: aportar lo necesario y aprovecharlo bien.",
      },
      {
        eyebrow: "MICROALGAS · BIOTECNOLOGÍA",
        title: "Pequeños organismos, gran biomasa",
        blocks: [
          {
            title: "La fábrica biológica",
            body: "Las microalgas son organismos fotosintéticos microscópicos que usan luz, dióxido de carbono y nutrientes para producir biomasa. Esa biomasa puede ser rica en proteínas, nutrientes, pigmentos y otros compuestos bioactivos de interés biotecnológico.",
          },
          {
            title: "Arthrospira maxima",
            body: "El proyecto trabaja con Arthrospira maxima, conocida comercialmente como espirulina. Técnicamente es una cianobacteria fotosintética, no una microalga verdadera, aunque suele estudiarse y cultivarse junto con ellas. La biomasa usada en el proyecto alcanzó 45,2 % de proteína.",
          },
          {
            title: "Cultivar para aprovechar",
            body: "En un biorreactor se controlan condiciones como luz, temperatura, nutrientes y aireación para producir biomasa de manera estable. Esa capacidad de generar biomasa rica en nutrientes y compuestos bioactivos es precisamente lo que la hace interesante para formular biofertilizantes.",
          },
        ],
        key: "CLAVE · Lo valioso no es solo que estos organismos hagan fotosíntesis: es la biomasa útil que podemos cultivar y aprovechar.",
      },
      {
        eyebrow: "DEL BIORREACTOR A LA PLANTA",
        title: "Un biofertilizante sin células vivas",
        blocks: [
          {
            title: "Paso 1 · Producir",
            body: "Arthrospira maxima se cultiva bajo condiciones controladas. Después se cosecha y se seca para obtener biomasa concentrada. En la formulación, esa biomasa se mezcla con agentes estabilizantes hasta formar un gel homogéneo.",
          },
          {
            title: "Paso 2 · Conservar lo útil",
            body: "Durante el secado y procesamiento las células dejan de estar vivas, pero la biomasa conserva nutrientes, proteínas y otros compuestos aprovechables. Por eso el producto puede funcionar como biofertilizante sin que las cianobacterias tengan que revivir o colonizar las raíces.",
          },
          {
            title: "Paso 3 · Nutrir",
            body: "Sus beneficios provienen de su composición: puede aportar nutrientes y favorecer mecanismos de la planta relacionados con su absorción. La idea no es reemplazar a la planta ni su fotosíntesis, sino apoyar su nutrición.",
          },
        ],
        key: "CLAVE · La biomasa no necesita estar viva para conservar componentes que la planta puede aprovechar.",
      },
      {
        eyebrow: "AGUA · SUELO · ECOSISTEMAS",
        title: "Producir más no puede ser la única meta",
        blocks: [
          {
            title: "Cuando sobra fertilizante",
            body: "El uso excesivo de insumos sintéticos puede contribuir a contaminación y deterioro de los ecosistemas. Un cultivo no necesariamente absorbe todo lo que se aplica: parte de los nutrientes puede perderse y desplazarse fuera del sitio de cultivo.",
          },
          {
            title: "La escorrentía conecta el cultivo con su entorno",
            body: "El agua que fluye sobre la superficie puede transportar nutrientes hacia fuentes de agua cercanas. Allí, un exceso de nutrientes puede alterar la calidad del agua y afectar los ecosistemas. El impacto, por tanto, no termina en el borde del cultivo.",
          },
          {
            title: "Pensar a largo plazo",
            body: "Cuidar agua, suelo y ecosistemas también protege la agricultura futura. Degradar los recursos que sostienen la producción compromete nuestra capacidad de seguir produciendo alimentos. Por eso una agricultura sostenible busca productividad y responsabilidad ambiental al mismo tiempo.",
          },
        ],
        key: "CLAVE · Reducir pérdidas y contaminación protege tanto a los ecosistemas como a la producción agrícola de mañana.",
      },
      {
        eyebrow: "CIENCIA LOCAL · FUTURO",
        title: "Medellín pone la idea a prueba",
        blocks: [
          {
            title: "No basta con que algo sea natural",
            body: "Antes de recomendar un biofertilizante hay que evaluar su calidad. El proyecto realiza caracterización fisicoquímica y microbiológica y ensayos de estabilidad para comprobar que el producto sea seguro, estable y adecuado para su uso.",
          },
          {
            title: "Un experimento en condiciones reales",
            body: "En el Laboratorio de Agricultura Urbana del Jardín Botánico se compara el crecimiento de plantas tratadas con el de un grupo control. Así se puede medir con evidencia si el biofertilizante realmente produce el efecto esperado.",
          },
          {
            title: "¿Por qué desarrollarlo aquí?",
            body: "Crear y probar bioinsumos en Medellín permite generar conocimiento y soluciones adaptadas a necesidades locales, con la meta de reducir impactos ambientales y fortalecer una agricultura urbana más limpia, eficiente y productiva. Esa es la investigación que inspira Verde Origen.",
          },
        ],
        key: "CLAVE · Innovación local significa probar, medir y mejorar soluciones pensadas para nuestro propio contexto.",
      },
    ],
  },
  en: {
    sceneLabel: "PROJECT ARCHIVE",
    title: "The story behind Green Origin",
    intro:
      "What you discover here will return later, transformed into questions, during your run.",
    previous: "PREVIOUS",
    next: "NEXT",
    back: "BACK",
    finish: "READY TO PLAY",
    page: "FILE",
    pages: [
      {
        eyebrow: "MEDELLÍN · URBAN AGRICULTURE",
        title: "A city that grows food, too",
        blocks: [
          {
            title: "The challenge",
            body: "Urban agriculture means growing food within or near cities. It brings production closer to the people who eat that food and makes use of gardens, community plots, and other urban spaces.",
          },
          {
            title: "What does sustainable mean?",
            body: "Meeting today's needs without compromising the resources or well-being of future generations. In agriculture, that means producing effectively while reducing impacts on water, soil, and ecosystems.",
          },
          {
            title: "The mission in Medellín",
            body: "Universidad CES, funded by Ruta N with the Medellín Botanical Garden as a partner, is evaluating a locally developed biofertilizer in the Urban Agriculture Laboratory. The goal is to find clean, effective solutions adapted to the city's needs.",
          },
        ],
        key: "KEY · Urban agriculture = growing within or near a city. Sustainability = producing today without closing off tomorrow's possibilities.",
      },
      {
        eyebrow: "BIOINPUTS · PLANT NUTRITION",
        title: "What is a biofertilizer?",
        blocks: [
          {
            title: "Nutrition from biological sources",
            body: "A biofertilizer is an agricultural input of biological origin. It can provide nutrients and natural compounds that support plant growth and development. It is not simply a synthetic fertilizer under a different name.",
          },
          {
            title: "Nutrients + bioactive compounds",
            body: "Nutrients are materials a plant needs to grow. A bioactive compound is a natural substance that can influence plant processes. A biofertilizer can supply nutrients while also supporting processes that improve nutrient uptake.",
          },
          {
            title: "Why is that useful?",
            body: "Biological alternatives can help reduce dependence on synthetic inputs. The goal is not to apply more product, but to provide what is needed: enough to nourish the crop without wasting resources or causing pollution.",
          },
        ],
        key: "KEY · Good nutrient management is about balance: supply what is needed and use it efficiently.",
      },
      {
        eyebrow: "MICROALGAE · BIOTECHNOLOGY",
        title: "Tiny organisms, valuable biomass",
        blocks: [
          {
            title: "The biological factory",
            body: "Microalgae are microscopic photosynthetic organisms that use light, CO₂, and nutrients to make biomass. That biomass can be rich in proteins, nutrients, pigments, and other bioactive compounds with biotechnological value.",
          },
          {
            title: "Arthrospira maxima",
            body: "The project uses Arthrospira maxima, commonly known as spirulina. Technically it is a photosynthetic cyanobacterium rather than a true microalga, although it is often studied and cultivated alongside microalgae. The project biomass reached 45.2% protein.",
          },
          {
            title: "Grow it to use it",
            body: "A bioreactor controls conditions such as light, temperature, nutrients, and aeration to produce biomass consistently. Its ability to generate biomass rich in nutrients and bioactive compounds is exactly what makes it interesting for biofertilizer development.",
          },
        ],
        key: "KEY · The value is not merely that these organisms photosynthesize, but the useful biomass we can grow and put to work.",
      },
      {
        eyebrow: "FROM BIOREACTOR TO PLANT",
        title: "A biofertilizer without living cells",
        blocks: [
          {
            title: "Step 1 · Produce",
            body: "Arthrospira maxima is grown under controlled conditions. It is then harvested and dried to obtain concentrated biomass. During formulation, that biomass is mixed with stabilizing agents to create a homogeneous gel.",
          },
          {
            title: "Step 2 · Keep what matters",
            body: "The cells are no longer alive after drying and processing, but the biomass retains nutrients, proteins, and other useful compounds. The product can therefore work as a biofertilizer without cyanobacteria reviving or colonizing the roots.",
          },
          {
            title: "Step 3 · Nourish",
            body: "Its benefits come from its composition: it can provide nutrients and support plant mechanisms related to nutrient uptake. The goal is not to replace the plant or its photosynthesis, but to support its nutrition.",
          },
        ],
        key: "KEY · Biomass does not need to be alive to retain components a plant can use.",
      },
      {
        eyebrow: "WATER · SOIL · ECOSYSTEMS",
        title: "Production cannot be the only goal",
        blocks: [
          {
            title: "When fertilizer is excessive",
            body: "Excessive use of synthetic inputs can contribute to pollution and ecosystem degradation. A crop does not necessarily absorb everything applied to it: some nutrients can be lost and move beyond the growing area.",
          },
          {
            title: "Runoff connects a crop to its surroundings",
            body: "Water flowing over the surface can carry nutrients into nearby water sources. There, excess nutrients can degrade water quality and affect ecosystems. The impact of nutrient management therefore does not end at the edge of a crop.",
          },
          {
            title: "Think long term",
            body: "Protecting water, soil, and ecosystems also protects future agriculture. Degrading the resources that support production compromises our ability to keep producing food. Sustainable agriculture therefore seeks productivity and environmental responsibility together.",
          },
        ],
        key: "KEY · Reducing losses and pollution protects both ecosystems and tomorrow's agricultural production.",
      },
      {
        eyebrow: "LOCAL SCIENCE · THE FUTURE",
        title: "Medellín puts the idea to the test",
        blocks: [
          {
            title: "Natural is not enough",
            body: "Before recommending a biofertilizer, its quality must be evaluated. The project uses physicochemical and microbiological characterization plus stability tests to check that the product is safe, stable, and suitable for use.",
          },
          {
            title: "A real-world experiment",
            body: "At the Botanical Garden's Urban Agriculture Laboratory, the growth of treated plants is compared with a control group. This makes it possible to measure, with evidence, whether the biofertilizer truly produces the expected effect.",
          },
          {
            title: "Why develop it here?",
            body: "Creating and testing bioinputs in Medellín generates knowledge and solutions adapted to local needs, with the goal of reducing environmental impacts and strengthening cleaner, more efficient, productive urban agriculture. That research is what inspired Green Origin.",
          },
        ],
        key: "KEY · Local innovation means testing, measuring, and improving solutions designed for our own context.",
      },
    ],
  },
};

export default class HistoryScene extends Phaser.Scene {
  constructor() {
    super("HistoryScene");
  }

  preload() {
    this.load.image("historyBG", "/assets/titleBG.png");
    this.load.audio("historyTap", "/assets/tap.m4a");
    this.load.audio("historySelected", "/assets/selected.m4a");
  }

  create() {
    window.dispatchEvent(
      new CustomEvent("biorev-mobile-ui", {
        detail: {
          mode: "hidden",
          // Keep the full reading canvas clear on phones; desktop side branding remains.
          hideRightPanel: true,
          visibleControls: [],
          powerStates: { one: false, two: false, three: false },
        },
      }),
    );

    const controlsUI = document.getElementById("main-scene-controls-ui");
    if (controlsUI) controlsUI.remove();

    const rightPanel = document.getElementById("right-ui-panel");
    if (rightPanel) rightPanel.style.visibility = "visible";

    const gameplayContent = document.getElementById(
      "right-panel-gameplay-content",
    );
    if (gameplayContent) gameplayContent.style.display = "none";

    this.lang =
      localStorage.getItem("agritech-language") === "en" ? "en" : "es";
    this.copy = HISTORY_CONTENT[this.lang];
    this.pageIndex = 0;
    this.pageObjects = [];

    this.add.image(400, 300, "historyBG").setDisplaySize(800, 600);
    this.add.rectangle(400, 300, 800, 600, 0x06130c, 0.74);

    // Top band: visually links the archive to the green/yellow game HUD.
    this.add.rectangle(400, 42, 800, 84, 0x0b1510, 0.94);
    this.add.rectangle(400, 84, 800, 2, 0xa3e635, 0.75);

    this.add
      .text(44, 12, this.copy.sceneLabel, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "12px",
        color: "#bef264",
        fontStyle: "bold",
        letterSpacing: 1.5,
      })
      .setDepth(3);

    this.add
      .text(44, 30, this.copy.title, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "23px",
        color: "#ecfccb",
      })
      .setDepth(3);

    this.add
      .text(44, 61, this.copy.intro, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "10.5px",
        color: "#cbd5e1",
        fontStyle: "600",
      })
      .setDepth(3);

    this.progressText = this.add
      .text(756, 24, "", {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "13px",
        color: "#fde68a",
        fontStyle: "bold",
      })
      .setOrigin(1, 0)
      .setDepth(3);

    this.createNavigation();
    this.renderPage();

    this.keyLeft = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.LEFT,
    );
    this.keyRight = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.RIGHT,
    );
    this.keyEsc = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.ESC,
    );

    this.keyLeft.on("down", () => this.changePage(-1));
    this.keyRight.on("down", () => this.changePage(1));
    this.keyEsc.on("down", () => this.returnToTitle());
  }

  createNavigation() {
    this.backButton = this.makeNavButton(92, 558, 108, this.copy.back, () =>
      this.returnToTitle(),
    );

    this.previousButton = this.makeNavButton(
      250,
      558,
      136,
      this.copy.previous,
      () => this.changePage(-1),
    );

    this.nextButton = this.makeNavButton(
      680,
      558,
      160,
      this.copy.next,
      () => this.changePage(1),
      true,
    );
  }

  makeNavButton(x, y, width, label, callback, primary = false) {
    const baseColor = primary ? 0x4d7c0f : 0x132219;
    const hoverColor = primary ? 0x65a30d : 0x1e3526;
    const borderColor = primary ? 0xd9f99d : 0x6f8f5b;

    const rect = this.add
      .rectangle(x, y, width, 42, baseColor, 0.96)
      .setStrokeStyle(2, borderColor)
      .setDepth(10)
      .setInteractive({ useHandCursor: true });

    const text = this.add
      .text(x, y, label, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "13px",
        color: primary ? "#ffffff" : "#e5e7eb",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(11);

    rect.on("pointerover", () => {
      rect.setFillStyle(hoverColor, 1);
      this.sound.play("historyTap", {
        volume:
          Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") *
          0.3,
      });
    });

    rect.on("pointerout", () => rect.setFillStyle(baseColor, 0.96));
    rect.on("pointerdown", callback);

    return { rect, text, primary, baseColor };
  }

  setButtonState(button, enabled, label) {
    button.rect.setVisible(enabled);
    button.text.setVisible(enabled);
    if (label) button.text.setText(label);
  }

  addPageObject(gameObject) {
    this.pageObjects.push(gameObject);
    return gameObject;
  }

  addRichBody(x, y, text, maxWidth = 650) {
    const container = this.add.container(x, y);

    const baseStyle = {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: "13.5px",
      color: "#e5e7eb",
    };

    const lineHeight = 20;
    const speciesName = "Arthrospira maxima";

    // Keep the scientific name together as one token.
    const preparedText = text.replace(
      /Arthrospira maxima/g,
      "Arthrospira_maxima",
    );

    const words = preparedText.split(/\s+/);

    const spaceProbe = this.add.text(0, 0, " ", baseStyle);
    const spaceWidth = spaceProbe.width;
    spaceProbe.destroy();

    let cursorX = 0;
    let cursorY = 0;

    words.forEach((rawWord) => {
      const isSpecies = rawWord.includes("Arthrospira_maxima");

      const displayWord = rawWord.replace("Arthrospira_maxima", speciesName);

      const word = this.add.text(0, 0, displayWord, {
        ...baseStyle,
        fontStyle: isSpecies ? "italic" : "normal",
      });

      if (cursorX > 0 && cursorX + word.width > maxWidth) {
        cursorX = 0;
        cursorY += lineHeight;
      }

      word.setPosition(cursorX, cursorY);
      container.add(word);

      cursorX += word.width + spaceWidth;
    });

    container.setSize(maxWidth, cursorY + lineHeight);

    return container;
  }

  renderPage() {
    this.pageObjects.forEach((object) => object.destroy());
    this.pageObjects = [];

    const page = this.copy.pages[this.pageIndex];
    const total = this.copy.pages.length;

    this.progressText.setText(
      `${this.copy.page} ${this.pageIndex + 1} / ${total}`,
    );

    // Reading card.
    this.addPageObject(
      this.add
        .rectangle(400, 310, 716, 444, 0x101c15, 0.96)
        .setStrokeStyle(2, 0x405747, 1)
        .setDepth(2),
    );

    // Left accent resembles the growth/progress language of the game.
    this.addPageObject(
      this.add.rectangle(51, 310, 5, 406, 0x65a30d, 0.95).setDepth(3),
    );

    this.addPageObject(
      this.add
        .text(72, 103, page.eyebrow, {
          fontFamily: '"Montserrat", sans-serif',
          fontSize: "11px",
          color: "#a3e635",
          fontStyle: "bold",
          letterSpacing: 1.1,
        })
        .setDepth(4),
    );

    this.addPageObject(
      this.add
        .text(72, 122, page.title, {
          fontFamily: '"Russo One", sans-serif',
          fontSize: "27px",
          color: "#f7fee7",
        })
        .setDepth(4),
    );

    let y = 168;

    page.blocks.forEach((block, index) => {
      const blockTitle = this.addPageObject(
        this.add
          .text(72, y, block.title, {
            fontFamily: '"Montserrat", sans-serif',
            fontSize: "14px",
            color: index === 1 ? "#fde68a" : "#d9f99d",
            fontStyle:
              block.title === "Arthrospira maxima" ? "bold italic" : "bold",
          })
          .setDepth(4),
      );

      y += blockTitle.height + 5;

      const body = this.addPageObject(
        this.addRichBody(72, y, block.body, 650).setDepth(4),
      );

      y += body.height + 12;
    });

    const keyY = Math.min(Math.max(y + 2, 450), 488);

    this.addPageObject(
      this.add
        .rectangle(400, keyY + 18, 650, 52, 0x263814, 0.86)
        .setStrokeStyle(1, 0x84cc16, 0.85)
        .setDepth(3),
    );

    this.addPageObject(
      this.add
        .text(400, keyY + 18, page.key, {
          fontFamily: '"Montserrat", sans-serif',
          fontSize: "12.5px",
          color: "#ecfccb",
          fontStyle: "bold",
          align: "center",
          wordWrap: { width: 612, useAdvancedWrap: true },
        })
        .setOrigin(0.5)
        .setDepth(4),
    );

    // Six progress nodes, like collected checkpoints.
    for (let i = 0; i < total; i += 1) {
      const active = i <= this.pageIndex;
      this.addPageObject(
        this.add
          .circle(
            390 + i * 21,
            558,
            i === this.pageIndex ? 5 : 3.5,
            active ? 0xa3e635 : 0x405747,
            1,
          )
          .setDepth(12),
      );
    }

    this.setButtonState(this.previousButton, this.pageIndex > 0);

    const isLast = this.pageIndex === total - 1;
    this.nextButton.text.setText(isLast ? this.copy.finish : this.copy.next);
  }

  changePage(direction) {
    const nextIndex = Phaser.Math.Clamp(
      this.pageIndex + direction,
      0,
      this.copy.pages.length - 1,
    );

    if (nextIndex === this.pageIndex) {
      if (direction > 0 && this.pageIndex === this.copy.pages.length - 1) {
        this.returnToTitle();
      }
      return;
    }

    this.sound.play("historySelected", {
      volume:
        Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 0.4,
    });

    this.pageIndex = nextIndex;
    this.renderPage();
  }

  returnToTitle() {
    this.scene.start("TitleScene");
  }
}
